import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconCheckCircle, IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { digitsOnly, groupThousands } from '../../../common/utils/money';
import ClientFarmSearch from '../../clients/forms/search';
import ProjectedCrossForm from '../../projected-crosses/forms/projected-cross-form';
import { CalfSearch, CrossSearch } from './pickers';
import { useCreateSale, useUpdateSale } from '../contexts/sales';

import '../styles/sales.css';

const today = () => new Date().toLocaleDateString('en-CA'); // wall-clock YYYY-MM-DD (0004)

// Input de dinero amigable: prefijo "$", miles con separador; guarda solo dígitos.
// Exportado para reusarlo en el panel de abonos (payments-panel) sin duplicar.
export function MoneyInput({ value, onChange, disabled, placeholder }) {
    return (
        <span className="sale-money">
            <span className="sale-money__sign">$</span>
            <input type="text" inputMode="numeric" value={groupThousands(value)} placeholder={placeholder} onChange={(e) => onChange(digitsOnly(e.target.value))} disabled={disabled} />
        </span>
    );
}

const calfLabel  = (c) => c?.name || c?.number || (c?.id ? `#${c.id}` : '—');
const crossLabel = (c) => c?.code || c?.name || (c?.id ? `#${c.id}` : '—');

// Registro de venta POLIMÓRFICA (admin-sales.md, 0058): en ALTA se elige el comprador
// y el objeto — una CRÍA real (calf_id) o un CRUCE planeado (projected_cross_id, reserva
// un cupo). En EDICIÓN el contrato permite comprador/fecha/valor/notas; el OBJETO es
// inmutable (reconciliar reserva→cría va por su propio flujo).
export default function SaleForm({ sale, onClose, onSaved }) {
    const editing = !!sale;
    const { mutateAsync: create, isPending: creating } = useCreateSale();
    const { mutateAsync: update, isPending: updating } = useUpdateSale(sale?.id);
    const busy = creating || updating;
    const cardRef = useModalDialog(onClose);

    const [mode, setMode] = useState(sale?.projected_cross ? 'project' : 'calf'); // 'calf' | 'project'
    const [creatingCross, setCreatingCross] = useState(false);
    const [buyer, setBuyer] = useState(sale?.buyer || null);
    const [calf, setCalf] = useState(sale?.calf || null);
    const [cross, setCross] = useState(sale?.projected_cross || null);
    const [date, setDate] = useState(sale?.date || today());
    const [value, setValue] = useState(sale?.agreed_value != null ? digitsOnly(String(sale.agreed_value)) : '');
    const [remarks, setRemarks] = useState(typeof sale?.remarks === 'string' ? sale.remarks : '');

    // PLAN de financiación PROVISIONAL (admin-sales.md, 0064 · Q-199) — todos opcionales.
    // Los ABONOS NO viven acá: son un sub-recurso (payments-panel). El sistema no calcula
    // dinero (0001); el vendedor ingresa/recalcula nº y valor de cuota.
    const [invoiceNumber, setInvoiceNumber] = useState(typeof sale?.purchase_invoice_number === 'string' ? sale.purchase_invoice_number : '');
    const [installments, setInstallments] = useState(sale?.installments_count != null ? String(sale.installments_count) : '');
    const [installmentValue, setInstallmentValue] = useState(sale?.installment_value != null ? digitsOnly(String(sale.installment_value)) : '');
    const [nextDueDate, setNextDueDate] = useState(sale?.next_due_date || '');

    const pickMode = (m) => { setMode(m); if (m === 'calf') setCross(null); else setCalf(null); };

    // Plan de financiación para el body (create y update). sold_by NUNCA se envía: lo asigna
    // el servidor desde el usuario autenticado (0064). Editar cuota/nº limpia needs_recalculation
    // en el backend. Vacío → null.
    const financingPayload = () => ({
        purchase_invoice_number: invoiceNumber.trim() || null,
        installments_count: installments !== '' ? Math.trunc(Number(installments)) : null,
        installment_value: installmentValue !== '' ? Number(installmentValue) : null,
        next_due_date: nextDueDate || null,
    });

    const submit = async () => {
        if (!buyer) { toast.error('Elegí el comprador'); return; }
        if (!date) { toast.error('La fecha es obligatoria'); return; }

        if (editing) {
            const payload = {
                buyer_id: buyer.id,
                date,
                agreed_value: value !== '' && value != null ? Number(value) : null,
                remarks: remarks.trim() || null,
                ...financingPayload(),
            };
            try {
                await update(payload);
                toast.success('Venta actualizada');
                onSaved?.();
            } catch (er) {
                if (er?.status === 409 && (er?.code === 'requires_reversal' || er?.fields?.code === 'requires_reversal')) {
                    toast.error('La venta ya está contabilizada; reversá el asiento antes de editar.');
                } else if (er?.status !== 403) {
                    toast.error(er?.message || 'No se pudo actualizar la venta');
                }
            }
            return;
        }

        if (mode === 'calf' && !calf) { toast.error('Elegí la cría'); return; }
        if (mode === 'project' && !cross) { toast.error('Elegí el cruce planeado'); return; }
        const payload = { buyer_id: buyer.id, date, ...financingPayload() };
        if (mode === 'calf') payload.calf_id = calf.id; else payload.projected_cross_id = cross.id;
        if (value !== '' && value != null) payload.agreed_value = Number(value);
        if (remarks.trim()) payload.remarks = remarks.trim();
        try {
            await create(payload);
            toast.success(mode === 'calf' ? 'Venta registrada' : 'Reserva registrada');
            onSaved?.();
        } catch (er) {
            if (er?.status === 409) {
                toast.error(er?.message || (mode === 'calf' ? 'Esa cría ya tiene una venta activa' : 'Cupo del cruce lleno o ya reservaste este cruce'));
            } else if (er?.status !== 403) {
                toast.error(er?.message || 'No se pudo registrar la venta');
            }
        }
    };

    const canSubmit = editing ? !!buyer : (!!buyer && !!date && (mode === 'calf' ? !!calf : !!cross));

    return (
        <>
        <div className="sale-modal" role="dialog" aria-modal="true" aria-label={editing ? 'Editar venta' : 'Registrar venta'}>
            <div className="sale-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="sale-modal__card" ref={cardRef}>
                <header className="sale-modal__head">
                    <div>
                        <h2 className="sale-modal__title">{editing ? 'Editar venta' : 'Registrar venta'}</h2>
                        <span className="sale-modal__sub">Cowre guarda la relación de venta — el monto es provisional.</span>
                    </div>
                    <button type="button" className="sale-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="sale-form">
                    {/* Tipo de objeto (solo en alta; el objeto es inmutable al editar) */}
                    {!editing && (
                        <div className="sale-form__field">
                            <span className="sale-form__label">¿Qué se vende?</span>
                            <div className="sale-form__seg" role="group" aria-label="Tipo de venta">
                                <button type="button" className={`sale-form__seg-btn ${mode === 'calf' ? 'sale-form__seg-btn--active' : ''}`} onClick={() => pickMode('calf')} disabled={busy}>Cría</button>
                                <button type="button" className={`sale-form__seg-btn ${mode === 'project' ? 'sale-form__seg-btn--active' : ''}`} onClick={() => pickMode('project')} disabled={busy}>Cruce planeado</button>
                            </div>
                        </div>
                    )}

                    {/* Comprador (editable en ambos) */}
                    <div className="sale-form__field">
                        <span className="sale-form__label">Comprador</span>
                        {buyer ? (
                            <span className="sale-form__picked">
                                <IconCheckCircle /> {buyer.name || `Cliente #${buyer.id}`}
                                <button type="button" className="sale-form__clear" onClick={() => setBuyer(null)} disabled={busy} aria-label="Cambiar comprador">×</button>
                            </span>
                        ) : (
                            <ClientFarmSearch scope="clients" placeholder="Buscar cliente comprador…" onSelect={(c) => setBuyer(c)} />
                        )}
                    </div>

                    {/* Objeto: cría o cruce */}
                    <div className="sale-form__field">
                        <span className="sale-form__label">{mode === 'project' ? 'Cruce planeado' : 'Cría'}</span>
                        {editing ? (
                            <span className="sale-form__readonly">
                                {mode === 'project'
                                    ? `${crossLabel(cross)}${cross?.donor?.name || cross?.sire?.name ? ` · ${cross?.donor?.name || '—'} × ${cross?.sire?.name || '—'}` : ''}`
                                    : `${calfLabel(calf)}${calf?.number && calf?.name ? ` · ${calf.number}` : ''}`}
                                <em className="sale-form__hint"> (no editable)</em>
                            </span>
                        ) : mode === 'project' ? (
                            cross ? (
                                <span className="sale-form__picked">
                                    <IconCheckCircle /> {crossLabel(cross)}
                                    <button type="button" className="sale-form__clear" onClick={() => setCross(null)} disabled={busy} aria-label="Cambiar cruce">×</button>
                                </span>
                            ) : (
                                <>
                                    <CrossSearch onSelect={(c) => setCross(c)} disabled={busy} />
                                    <button type="button" className="sale-form__link" onClick={() => setCreatingCross(true)} disabled={busy}>+ Crear cruce planeado</button>
                                </>
                            )
                        ) : (
                            calf ? (
                                <span className="sale-form__picked">
                                    <IconCheckCircle /> {calfLabel(calf)}{calf.number ? ` · ${calf.number}` : ''}
                                    <button type="button" className="sale-form__clear" onClick={() => setCalf(null)} disabled={busy} aria-label="Cambiar cría">×</button>
                                </span>
                            ) : <CalfSearch onSelect={(a) => setCalf(a)} disabled={busy} />
                        )}
                        {!editing && mode === 'project' && <span className="sale-form__note">Reserva un cupo del cruce; se materializa en una cría al nacer (reconciliar).</span>}
                    </div>

                    <div className="sale-form__row">
                        <label className="sale-form__field">
                            <span className="sale-form__label">Fecha de venta</span>
                            <input type="date" max={today()} value={date} onChange={(e) => setDate(e.target.value)} disabled={busy} />
                        </label>
                        <div className="sale-form__field">
                            <span className="sale-form__label">Valor pactado</span>
                            <MoneyInput value={value} onChange={setValue} disabled={busy} placeholder="Opcional" />
                        </div>
                    </div>

                    {/* PLAN de financiación provisional (0064 · Q-199): datos registrados, nunca
                        un saldo calculado. Los abonos se cargan aparte (panel de Abonos). */}
                    <div className="sale-form__section">
                        <span className="sale-form__section-title">Plan de financiación (provisional)</span>

                        {editing && sale?.needs_recalculation && (
                            <p className="sale-form__callout">
                                Se registró un abono: ajustá el nº o el valor de la cuota y guardá para recalcular el plan.
                                El aviso “Requiere recálculo” se quita al guardar.
                            </p>
                        )}

                        <label className="sale-form__field">
                            <span className="sale-form__label">Nº factura de compra</span>
                            <input type="text" maxLength={60} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} disabled={busy} placeholder="Opcional" />
                        </label>

                        <div className="sale-form__row">
                            <label className="sale-form__field">
                                <span className="sale-form__label">Nº de cuotas</span>
                                <input type="number" min="0" step="1" inputMode="numeric" value={installments} onChange={(e) => setInstallments(e.target.value)} disabled={busy} placeholder="Opcional" />
                            </label>
                            <div className="sale-form__field">
                                <span className="sale-form__label">Valor cuota</span>
                                <MoneyInput value={installmentValue} onChange={setInstallmentValue} disabled={busy} placeholder="Opcional" />
                            </div>
                        </div>

                        <label className="sale-form__field">
                            <span className="sale-form__label">Próxima fecha de vencimiento</span>
                            <input type="date" value={nextDueDate} onChange={(e) => setNextDueDate(e.target.value)} disabled={busy} />
                            <span className="sale-form__note">Si esta fecha ya pasó, la venta se marca “Atrasada”.</span>
                        </label>
                    </div>

                    <label className="sale-form__field">
                        <span className="sale-form__label">Notas</span>
                        <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} placeholder="Opcional" />
                    </label>

                    {/* Provenance solo lectura — el servidor la asigna desde el staff autenticado (0064). */}
                    {editing && sale?.sold_by?.name && (
                        <div className="sale-form__field">
                            <span className="sale-form__label">Venta realizada por</span>
                            <span className="sale-form__readonly">{sale.sold_by.name}</span>
                        </div>
                    )}
                </div>

                <footer className="sale-modal__foot">
                    <button type="button" className="sale-btn sale-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="sale-btn sale-btn--primary" onClick={submit} disabled={busy || !canSubmit}>
                        {busy ? 'Guardando…' : editing ? 'Guardar cambios' : mode === 'project' ? 'Registrar reserva' : 'Registrar venta'}
                    </button>
                </footer>
            </div>
        </div>

        {creatingCross && (
            <ProjectedCrossForm
                onClose={() => setCreatingCross(false)}
                onCreated={(c) => { setCross(c); setCreatingCross(false); }}
            />
        )}
        </>
    );
}
